import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { AnimatedSection, AnimatedText, itemVariants, sectionVariants } from '../animations';

const IMAGE_BASE_URL = 'https://smartsolutionslk.com/uploads/';

// All available categories
const CATEGORIES = [
    'All',
    'Web & App Engineering',
    'AI & Automation',
    'E-commerce Platforms',
    'Digital Marketing',
    'Startup Acceleration',
    'Smart Technologies',
];

const toSlug = (cat) => cat.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

const PortfolioSection = ({ allProjects }) => {
    const [activeCategory, setActiveCategory] = useState('All');

    // Helper function to get proper image URL
    const getImageSrc = (url) => {
        if (!url) return 'https://placehold.co/1200x800/1f2937/FFFFFF?text=No+Image';
        if (url.startsWith('data:image')) return url;
        if (url.startsWith('http')) return url;
        return `${IMAGE_BASE_URL}${url}`;
    };

    // Filter projects by active category, then take 6 most recent
    const filteredProjects = useMemo(() => {
        const filtered = activeCategory === 'All'
            ? allProjects
            : allProjects.filter(p => p.category === activeCategory);
        return filtered.slice(0, 6);
    }, [allProjects, activeCategory]);

    // Build category list from real data + always include "All"
    const availableCategories = useMemo(() => {
        const fromData = [...new Set(allProjects.map(p => p.category).filter(Boolean))];
        // Keep the order from CATEGORIES, only show categories that exist in data or are "All"
        return CATEGORIES.filter(c => c === 'All' || fromData.includes(c));
    }, [allProjects]);

    return (
        <AnimatedSection id="work" className="bg-white dark:bg-black text-slate-900 dark:text-white">
            <div className="container mx-auto px-6">
                {/* Section Header */}
                <div className="text-center mb-10">
                    <AnimatedText
                        text="Recent Work We're Proud Of."
                        className="text-4xl md:text-6xl font-bold"
                        variants={sectionVariants}
                    />
                </div>

                {/* ── Category Filter Bar ── */}
                <motion.div
                    className="flex flex-wrap justify-center gap-3 mb-12"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                >
                    {availableCategories.map((cat) => (
                        <motion.button
                            key={cat}
                            onClick={() => setActiveCategory(cat)}
                            className={`relative px-5 py-2 rounded-full text-sm font-semibold transition-all duration-300 border ${
                                activeCategory === cat
                                    ? 'bg-cyan-500 text-black border-cyan-500 shadow-lg shadow-cyan-500/30'
                                    : 'bg-transparent text-slate-600 dark:text-gray-400 border-slate-300 dark:border-gray-700 hover:border-cyan-400 hover:text-cyan-500 dark:hover:text-cyan-400'
                            }`}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                        >
                            {activeCategory === cat && (
                                <motion.span
                                    layoutId="activePill"
                                    className="absolute inset-0 rounded-full bg-cyan-500"
                                    style={{ zIndex: -1 }}
                                    transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                                />
                            )}
                            {cat}
                        </motion.button>
                    ))}
                </motion.div>

                {/* ── Project Grid ── */}
                <AnimatePresence mode="wait">
                    <motion.div
                        key={activeCategory}
                        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.35, ease: 'easeOut' }}
                    >
                        {filteredProjects.length > 0 ? (
                            filteredProjects.map((project, index) => (
                                <motion.div
                                    key={project.id || index}
                                    variants={itemVariants}
                                    initial="hidden"
                                    animate="visible"
                                    transition={{ delay: index * 0.07 }}
                                    className="group flex flex-col h-full bg-white dark:bg-gray-900 rounded-xl overflow-hidden shadow-lg hover:shadow-2xl hover:shadow-cyan-500/10 transition-all duration-300 border border-slate-100 dark:border-gray-800 hover:border-cyan-500/40"
                                >
                                    {/* Image */}
                                    <div
                                        className="relative w-full overflow-hidden"
                                        style={{ paddingTop: '56.25%' }}
                                    >
                                        <img
                                            src={getImageSrc(project.image_url || project.image)}
                                            alt={project.title}
                                            className="absolute top-0 left-0 w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                                            onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.src = 'https://placehold.co/1200x800/1f2937/FFFFFF?text=Image+Not+Found';
                                            }}
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-br from-slate-800/80 via-slate-900/75 to-cyan-900/85 group-hover:opacity-0 transition-opacity duration-500" />
                                    </div>

                                    {/* Text */}
                                    <div className="p-6 flex flex-col flex-grow">
                                        <p className="text-cyan-600 dark:text-cyan-400 font-semibold text-sm">{project.category}</p>
                                        <h3 className="text-xl font-bold mt-1 text-slate-900 dark:text-white">{project.title}</h3>
                                    </div>
                                </motion.div>
                            ))
                        ) : (
                            <motion.div
                                className="col-span-3 text-center py-20 text-slate-400 dark:text-gray-500"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                            >
                                <p className="text-lg font-medium">No projects found in this category yet.</p>
                            </motion.div>
                        )}
                    </motion.div>
                </AnimatePresence>

                {/* ── See All Button ── */}
                <motion.div
                    className="flex justify-center mt-14"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.4 }}
                >
                    <Link
                        to={activeCategory === 'All' ? '/projects' : `/projects?category=${toSlug(activeCategory)}`}
                        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    >
                        <motion.span
                            className="inline-flex items-center gap-3 px-8 py-3.5 rounded-full border-2 border-cyan-500 text-cyan-500 dark:text-cyan-400 font-semibold text-base hover:bg-cyan-500 hover:text-black transition-all duration-300 shadow-md hover:shadow-cyan-500/30 group"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.97 }}
                        >
                            {activeCategory === 'All' ? 'See All Projects' : `See All ${activeCategory} Projects`}
                            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-200" />
                        </motion.span>
                    </Link>
                </motion.div>
            </div>
        </AnimatedSection>
    );
};

export default PortfolioSection;